<template>
    <Modal class="payment-error-modal modl--danger"
           data-component="PaymentErrorModal"
           icon="fa-solid fa-triangle-exclamation"
           icon-color="red"
           title="PAYMENT_ERROR.MODAL_TITLE">
        <ErrorMessage v-for="(error, i) in errors"
                      :key="i"
                      :error="error"
                      :show-icon="false">
            {{ error.errorID ? 'Error ID: ' + error.errorID : '' }}
        </ErrorMessage>

        <PageLoadingSpinner v-if="checkingSavedPayments" />
        <template v-else>
            <table v-if="actualPreviousPayments.length"
                   class="table table-striped payment-error-modal__payments-table">
                <thead>
                    <tr>
                        <th>{{ l('PAYMENT_ERROR.PAYMENTS_TAKEN_PREVIOUSLY') }}</th>
                        <th>{{ l('ORDER_PAYMENTS.AMOUNT') }}</th>
                        <th>{{ l('ORDER_PAYMENTS.STATUS') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <PaymentErrorPayment v-for="payment in actualPreviousPayments"
                                         :key="payment.ID"
                                         :payment="payment"
                                         :is-saved="savedPaymentThirdPartyIDs.includes(payment.thirdPartyID)" />
                </tbody>
            </table>

            <table v-if="cartPayments.length"
                   class="table table-striped payment-error-modal__payments-table">
                <thead>
                    <tr>
                        <th>{{ l('PAYMENT_ERROR.PAYMENTS_TAKEN_NOW') }}</th>
                        <th>{{ l('ORDER_PAYMENTS.AMOUNT') }}</th>
                        <th>{{ l('ORDER_PAYMENTS.STATUS') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <PaymentErrorPayment v-for="payment in cartPayments"
                                         :key="payment.ID"
                                         :payment="payment"
                                         :is-saved="savedPaymentThirdPartyIDs.includes(payment.thirdPartyID)" />
                </tbody>
            </table>

            <ExpectationsTable :data="expectationsData" />

            <div v-if="!hasUnsavedPayments"
                 class="alert alert-info mt-1">
                {{ l('PAYMENT_ERROR.OK_ALERT') }}
            </div>

            <p class="text-center mt-2">
                <small>{{ l('PAYMENT_ERROR.CONTACT') }}</small>
                <template v-if="currentDevice || cart?.thirdPartyRef">
                    <br />
                    <small v-if="currentDevice">
                        {{ l('PAYMENT_ERROR.DEVICE' ) }}: {{ currentDevice.ID }}
                    </small>
                    &nbsp;
                    <small v-if="cart?.thirdPartyRef">
                        {{ l('PAYMENT_ERROR.ORDER_REFERENCE') }}: {{ cart.thirdPartyRef }}
                    </small>
                </template>
            </p>
        </template>

        <template v-slot:footer>
            <div class="modl-footer">
                <!-- Abandon payments button -->
                <!-- This first button is a dangerous one. But it's not red because that makes it too enticing. -->
                <SquareBtn v-if="hasUnsavedPayments"
                           color="default"
                           class="cancel-btn"
                           data-analytics-id="Payment Error Modal / Abandon Payments"
                           size="lg"
                           @click="abandon">
                    <i class="fa-solid fa-diamond-exclamation"></i>
                    {{ l('PAYMENT_ERROR.ABANDON_BTN') }}
                </SquareBtn>

                <!-- Print receipt button -->
                <SquareBtn color="default"
                           data-analytics-id="Payment Error Modal / Reprint Receipt"
                           size="lg"
                           :loading="printing"
                           @click="print">
                    <i class="fa-solid fa-print"></i>
                    {{ l(cart.receiptPrintedAt ? 'PAYMENT_ERROR.REPRINT_BTN' : 'PAYMENT_ERROR.PRINT_BTN') }}
                </SquareBtn>

                <!-- Retry button -->
                <SquareBtn v-if="retryEnabled"
                           color="primary"
                           data-analytics-id="Payment Error Modal / Retry"
                           size="lg"
                           @click="retry">
                    <i class="fa-solid fa-arrow-rotate-right"></i>
                    {{ l('PAYMENT_ERROR.RETRY_BTN') }}
                </SquareBtn>

                <!-- View order button -->
                <SquareBtn v-if="viewOrderEnabled"
                           color="info"
                           data-analytics-id="Payment Error Modal / View Order"
                           size="lg"
                           @click="viewOrder">
                    <i class="fa-solid fa-magnifying-glass"></i>
                    {{ l('PAYMENT_ERROR.VIEW_ORDER_BTN') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>

/**
 * @typedef {object} PaymentErrorModalResult
 * @property {PaymentErrorModalAction} action - The action the user took to resolve the error.
 */

import BigNumber from 'bignumber.js';
import CheckoutErrorActions from '@/lib/Checkout/CheckoutErrorActions';
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import ExpectationsTable from '@/vue/components/Tables/ExpectationsTable';
import Modal from '@/vue/components/Modals/Modal';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PaymentErrorPayment from '@/epos/vue/modals/PaymentError/PaymentErrorPayment';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import SquareBtn from '@/vue/components/SquareBtn';
import TradingSessionEvent from '@/lib/TradingSessions/TradingSessionEvent';
import TradingSessionEventTypeEnun from '@/lib/TradingSessions/TradingSessionEventType';
import { container } from '@/container';
import { currencyFilter } from '@/vue/filters/currencyFilter';
import {
    getTotalOfCompletePaymentsIncludingTrustedPending,
    getTotalOfPayments,
} from '@/lib/Payments/paymentHelperFunctions';
import { mapState } from 'vuex';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

/**
 * This modal displays when there was an error saving payments against an order.
 * It fetches the order from the API to see which payments were successfully saved and which were not.
 * We display all the payments that were in the cart and indicate which were saved and which were not, matching
 * them by their thirdPartyID.
 * Note that the order may contain more payments than were in the cart if this is an adjustment and there
 * were payments from a previous order.
 */
export default {
    components: {
        ExpectationsTable,
        PaymentErrorPayment,
        ErrorMessage,
        PageLoadingSpinner,
        SquareBtn,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {Cart}
         */
        cart: {
            type: Object,
            required: true,
        },

        /**
         * @type {Order}
         */
        order: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            /**
             * Payments for this order loaded from the API.
             * This will include any previous payments if this is an adjustment.
             */
            savedPayments: [],

            /**
             * @type {boolean}
             */
            checkingSavedPayments: false,

            /**
             * @type {boolean}
             */
            printing: false,
        };
    },

    computed: {
        ...mapState(['currentDevice']),

        isAdjustment() {
            return this.order.isAdjustment();
        },

        cartPayments() {
            return this.cart.payments;
        },

        actualPreviousPayments() {
            // this.savedPayments will include all payments saved against the order,
            // including any from previous orders if this is an adjustment.
            // It's good to display those to the user too.
            // The savedPayments list may include a mix of payments from this cart and previous orders though.
            // Separate those out here by returning only payments without a thirdPartyID matching one in the cart.
            // There is a chance of a false positive here if for some reason the thirdPartyID wasn't saved.
            // But we have no other way of finding out specifically which order a payment was for.
            return this.savedPayments
                .filter((p) => {
                    return !this.cartPayments.find(
                        (cp) => cp.thirdPartyID === p.thirdPartyID,
                    );
                });
        },

        errors() {
            return this.cart.getUnresolvedPaymentSaveErrors();
        },

        /**
         * Returns a list of third party IDs of payments that have successfully been saved.
         *
         * @returns {string[]}
         */
        savedPaymentThirdPartyIDs() {
            return this.savedPayments.map((p) => p.thirdPartyID);
        },

        unsavedCartPayments() {
            return this.cartPayments
                // Filter to payments that are not found in the saved payments.
                .filter((p) => {
                    return !this.savedPayments.find(
                        (sp) => sp.thirdPartyID === p.thirdPartyID,
                    );
                });
        },

        /**
         * Returns a list of new payments that have not been saved against the order.
         * This is used to display the payments that were attempted but not saved.
         *
         * @returns {string[]}
         */
        unsavedPaymentThirdPartyIDs() {
            return this.unsavedCartPayments.map((p) => p.thirdPartyID);
        },

        expectedSavedPaymentsTotal() {
            // We always expect all payments to save correctly.
            let total = new BigNumber(0);

            // Add any payments taken in previous orders.
            if (this.cart.originalOrder) {
                total = total
                    .plus(this.cart.originalOrder.getTotalPaidIncludingTrustedPending());
            }

            // Add any payments taken in this order.
            total = total.plus(this.cartPaymentsTotal);

            return total.toFixed(2);
        },

        actualSavedPaymentsTotal() {
            return getTotalOfCompletePaymentsIncludingTrustedPending(this.savedPayments);
        },

        expectedUnsavedPaymentsTotal() {
            // We always expect all payments to save correctly.
            return 0;
        },

        actualUnsavedPaymentsTotal() {
            let total = new BigNumber(0);

            this.unsavedCartPayments.forEach((p) => {
                total = total.plus(p.amount);
            });

            return total.toFixed(2);
        },

        /**
         * Get the expected balance of the overall booking.
         * That is the total of items in the cart, minus any payments taken for this cart/order.
         * AND minus any payments taken in previous orders.
         *
         * This is not always expected to be 0.
         * We may expect there to be some outstanding balance if only a deposit was taken.
         *
         * @return {string}
         */
        expectedOrderBalance() {
            let balance = new BigNumber(this.cart.getTotal());

            // Deduct any payments taken in previous orders.
            if (this.cart.originalOrder) {
                balance = balance
                    .minus(this.cart.originalOrder.getTotalPaidIncludingTrustedPending());
            }

            // Deduct any payments taken in this order.
            balance = balance.minus(this.cartPaymentsTotal);

            return balance.toFixed(2);
        },

        actualOrderBalance() {
            let balance = new BigNumber(this.cart.getTotal());

            return balance
                .minus(this.orderPaymentsTotal)
                .toFixed(2);
        },

        cartPaymentsTotal() {
            return getTotalOfCompletePaymentsIncludingTrustedPending(this.cartPayments);
        },

        orderPaymentsTotal() {
            return getTotalOfCompletePaymentsIncludingTrustedPending(this.savedPayments);
        },

        expectationsData() {
            const data = [];

            data.push({
                label: 'PAYMENT_ERROR.TOTAL_PAYMENTS_SAVED',
                expected: currencyFilter(this.expectedSavedPaymentsTotal),
                actual: currencyFilter(this.actualSavedPaymentsTotal),
            });

            data.push({
                label: 'PAYMENT_ERROR.TOTAL_PAYMENTS_UNSAVED',
                expected: currencyFilter(this.expectedUnsavedPaymentsTotal),
                actual: currencyFilter(this.actualUnsavedPaymentsTotal),
            });

            data.push({
                label: 'PAYMENT_ERROR.OUTSTANDING_BALANCE',
                expected: currencyFilter(this.expectedOrderBalance),
                actual: currencyFilter(this.actualOrderBalance),
            });

            return data;
        },

        hasUnsavedPayments() {
            // Check if allowing the user to retry makes sense.
            // If everything adds up then there's no need to re-send any payments.
            return this.unsavedCartPayments.length > 0;
        },

        retryEnabled() {
            return this.hasUnsavedPayments;
        },

        viewOrderEnabled() {
            return !this.hasUnsavedPayments;
        },
    },

    async created() {
        // Ask the API for all payments that have been successfully saved against the order.
        // We'll use this to build a list of which payments have been successfully saved and
        // which have not.
        await this.loadOrderPayments(this.order.getOriginalRef());

        // Always print a receipt on error (if we haven't already printed one).
        if (!this.cart.receiptPrintedAt) {
            await this.print();
        }
    },

    methods: {
        async loadOrderPayments(bookingRef) {
            this.checkingSavedPayments = true;
            try {
                // Load all the payments saved against this order from the API.
                // Remember that payments are always linked to the original order's ID, even if this is an adjustment.
                this.savedPayments = await container.paymentService.getPaymentsByBookingRef(bookingRef);

                // Because we've just loaded the saved payments for the order we can also update the payments
                // in the cart to mark those successfully saved - by setting their ID.
                await container.cartService.modifyCurrentCart((cart) => {
                    this.savedPayments.forEach((payment) => {
                        const cartPayment = cart.payments.find((p) => p.thirdPartyID === payment.thirdPartyID);
                        if (cartPayment) {
                            cartPayment.ID = payment.ID;
                        }
                    });
                });
            } catch {
                // Assume no payments if there was an error.
                this.savedPayments = [];
            } finally {
                this.checkingSavedPayments = false;
            }
        },

        async abandon() {
            let privilegeResult;
            try {
                // Require supervisor confirmation to abandon the order.
                privilegeResult = await container.privilegesManager.requirePrivilege(PrivilegeNames.ABANDON_PAYMENTS);
            } catch {
                // No permission - quit.
                return;
            }

            // Prompt for confirmation.
            const confirmed = await container.dialogService.confirm('PAYMENT_ERROR.CONFIRM_ABANDON');
            if (!confirmed) {
                return;
            }

            // Log a trading session entry.
            // TODO: We could prompt for notes from the user such as what happened and store them as the narrative?
            const tradingSessionEvent = (new TradingSessionEvent(TradingSessionEventTypeEnun.ABANDON_PAYMENTS))
                .setOperatorID(container.userService.getUserID())
                .setManagerID(privilegeResult.grantedBy)
                .setAmount(
                    // Add up the amount of all unsaved payments.
                    getTotalOfPayments(this.unsavedCartPayments)
                )
                .setData({
                    thirdPartyIDs: this.unsavedPaymentThirdPartyIDs,
                });
            await container.tradingSessionManager.saveEvent(tradingSessionEvent);

            // Go to the order details page.
            container.navigationService.viewOrderDetails(this.order.getOriginalRef());

            this.resolve({
                action: CheckoutErrorActions.ABANDON,
            });
        },

        async print() {
            this.printing = true;
            try {
                await container.orderReceiptPrinter.printOrderReceipt(
                    this.order,
                    null,
                    null,
                    false,
                    {
                        errorIDs: this.cart.getErrorIDs(),
                    }
                );
                await container.cartService.modifyCurrentCart((cart) => {
                    cart.receiptPrintedAt = new Date();
                });
            } finally {
                this.printing = false;
            }
        },

        async retry() {
            this.resolve({
                action: CheckoutErrorActions.RETRY,
            });
        },

        async viewOrder() {
            // Go to the order details page.
            container.navigationService.viewOrderDetails(this.order.getOriginalRef());

            this.resolve({
                action: CheckoutErrorActions.VIEW_ORDER,
            });
        },
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../../less/variables.less";

.payment-error-modal {
    .table:not(:first-child) {
        margin-top: @gutter-width;
    }

    .label {
        margin-left: 5px;
    }

    .payment-error-modal__payments-table,
    .expectations-table {
        td:nth-child(n+2),
        th:nth-child(n+2) {
            width: 140px;
            white-space: nowrap;
            text-align: right;
        }
    }
}
</style>
